import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { projects } from '../mockData';

const statusColor = (status) =>
  status === 'Completed' ? 'bg-green-500' : status === 'In Progress' ? 'bg-blue-500' : 'bg-brand-red';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const categories = ['All', ...new Set(projects.map((p) => p.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ? projects : projects.filter((p) => p.category === activeCategory);

  return (
    <main data-testid="projects-page">
      <PageHero
        eyebrow="Projects"
        title="Our Work,"
        accent="Our Signature"
        description="A showcase of the projects that reflect our commitment to quality, sustainability, and community impact. Placeholder imagery is shown where project photography is not yet available."
      />

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          {/* Category Filter */}
          <Reveal className="flex flex-wrap gap-3 mb-14">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`project-filter-${cat.toLowerCase().replace(/[\s&]+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors border ${
                  activeCategory === cat
                    ? 'bg-brand-red text-white border-brand-red'
                    : 'bg-white text-charcoal border-gray-200 hover:border-brand-red hover:text-brand-red'
                }`}
              >
                {cat}
              </button>
            ))}
          </Reveal>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((project, index) => (
              <Reveal key={project.id} delay={index * 0.08}>
                <div
                  className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="relative h-64 overflow-hidden flex-shrink-0">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${statusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 bg-white flex-grow">
                    <div className="text-sm text-brand-red font-semibold mb-2">{project.category}</div>
                    <h3 className="text-xl font-bold text-charcoal mb-2 group-hover:text-brand-red transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{project.description}</p>
                    {(project.client || project.mainContractor || project.role) && (
                      <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5" data-testid={`project-details-${project.id}`}>
                        {project.client && (
                          <div className="text-xs text-gray-500">
                            <span className="font-semibold text-charcoal">Client / Authority:</span> {project.client}
                          </div>
                        )}
                        {project.mainContractor && (
                          <div className="text-xs text-gray-500">
                            <span className="font-semibold text-charcoal">Main Contractor:</span> {project.mainContractor}
                          </div>
                        )}
                        {project.role && (
                          <div className="text-xs text-gray-500">
                            <span className="font-semibold text-charcoal">Alakhe Conglomerate Role:</span> {project.role}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="text-center mt-16">
            <p className="text-gray-600 mb-6">Interested in what we can build for you?</p>
            <Button
              onClick={() => navigate('/contact')}
              data-testid="projects-cta-button"
              className="bg-brand-red hover:bg-red-700 text-white font-semibold text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all group"
            >
              Start a Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
